import { Error as Icon } from "@mos-cat/ds-icons";
import type { Theme } from "@mos-cat/ds-theme";
import { createComponent, createHook } from "reakit-system";
import type { BoxHTMLProps, BoxOptions } from "../Box/Box";
import { useBox } from "../Box/Box";
import type { TextHTMLProps, TextOptions, TextProps } from "../Text/Text";
import { useText } from "../Text/Text";
import { WHCM } from "../constants";

/**
 * @beta
 */
export type ErrorTextProps = Omit<TextProps, "lineHeight" | "fontSize" | "color" | "fontWeight">;
export type ErrorIconOption = BoxOptions & { theme: Theme };

const useErrorIcon = createHook<ErrorIconOption, BoxHTMLProps>({
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      display: "inline",
      verticalAlign: "middle",
      mr: "errorIconMarginRight",
      mt: "errorIconMarginTop",
      size: "1em",

      styles: {
        "& circle": {
          fill: "currentColor",
        },
        [WHCM]: {
          fill: "Highlight",
        },
      },
    };
  },
});

const ErrorIcon = createComponent<typeof Icon, ErrorIconOption>({
  useHook: useErrorIcon,
  as: Icon,
});

type ErrorTextOptions = Omit<TextOptions, "color" | "fontSize" | "lineHeight">;
const useErrorText = createHook<ErrorTextOptions, TextHTMLProps>({
  compose: [useText],
  useOptions(options) {
    return {
      ...options,
      color: "errorColor",
      fontSize: "errorFontSize",
      lineHeight: "errorLineHeight",
      styles: {
        display: "block",

        [WHCM]: {
          color: "Highlight",
        },
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      children: (
        <>
          <ErrorIcon />
          {props.children}
        </>
      ),
    };
  },
});

/**
 * @beta
 */
export const ErrorText = createComponent<"span", ErrorTextOptions>({
  as: "span",
  useHook: useErrorText,
});
